import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getRingSession } from "@/lib/ringApi";
import { useRoom } from "@/hooks/useRoom";
import { useT } from "@/i18n";
import { computeRemaining, fmtClock } from "@/lib/timer";
import { Logo } from "@/components/Logo";

const nameOf = (m, id) => {
  const a = m?.athletes?.[id];
  return a ? `${a.first_name} ${a.last_name}` : "—";
};

export default function Monitor() {
  const { ringId } = useParams();
  const nav = useNavigate();
  const t = useT();
  const session = getRingSession();
  const [remaining, setRemaining] = useState(0);
  const tick = useRef();

  useEffect(() => {
    if (!session || session.ring.id !== ringId) nav(`/ring-login?ring_id=${ringId}`);
    /* eslint-disable-next-line */
  }, [ringId]);

  const { snapshot } = useRoom(ringId, session?.token);
  const match = snapshot?.match;

  useEffect(() => {
    clearInterval(tick.current);
    if (match?.discipline === "combat" && match?.state?.timer) {
      tick.current = setInterval(() => setRemaining(computeRemaining(match.state.timer)), 150);
    }
    return () => clearInterval(tick.current);
  }, [match]);

  if (!session) return null;

  if (!match) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center text-white">
        <Logo size={64} className="text-brand-yellow mb-6" />
        <div className="font-heading font-extrabold text-5xl">{session.ring.name}</div>
        <div className="text-neutral-500 mt-3 uppercase tracking-[0.3em] text-sm">{t("mon.waiting")}</div>
      </div>
    );
  }

  if (match.discipline === "combat") return <CombatMonitor match={match} remaining={remaining} />;
  return <PoomsaeMonitor match={match} />;
}

function CombatSide({ color, name, data }) {
  const t = useT();
  const isRed = color === "red";
  const bg = isRed ? "bg-hong" : "bg-chung";
  return (
    <div className={`flex-1 ${bg} flex flex-col items-center justify-between py-[4vh] px-[2vw] relative`}>
      <div className="font-name text-white font-extrabold uppercase text-center leading-none tracking-tight" style={{ fontSize: "5vw" }}>{name}</div>
      <div key={data.score} className="font-score text-white leading-none tabular animate-score" style={{ fontSize: "26vw", textShadow: "0 0 40px rgba(0,0,0,0.4)" }} data-testid={`monitor-score-${color}`}>{data.score}</div>
      <div className="flex gap-[2vw] text-white/95">
        <div className="text-center">
          <div className="font-score tabular" style={{ fontSize: "4vw" }} data-testid={`monitor-warn-light-${color}`}>{data.warnings_light}</div>
          <div className="uppercase tracking-widest" style={{ fontSize: "0.9vw" }}>{t("mon.warnLight")}</div>
        </div>
        <div className="text-center">
          <div className="font-score tabular" style={{ fontSize: "4vw" }} data-testid={`monitor-warn-severe-${color}`}>{data.warnings_severe}</div>
          <div className="uppercase tracking-widest" style={{ fontSize: "0.9vw" }}>{t("mon.warnSevere")}</div>
        </div>
      </div>
    </div>
  );
}

function CombatMonitor({ match, remaining }) {
  const t = useT();
  const st = match.state;
  return (
    <div className="h-screen w-screen bg-black flex relative overflow-hidden">
      <CombatSide color="red" name={nameOf(match, match.red_athlete_id)} data={st.red} />
      <CombatSide color="blue" name={nameOf(match, match.blue_athlete_id)} data={st.blue} />
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 flex flex-col items-center">
        <div className="bg-black px-[3vw] py-[1vh] rounded-t-2xl border-x border-t border-neutral-800 flex flex-col items-center">
          <div className="text-white font-mono-t font-bold tabular leading-none" style={{ fontSize: "8vw" }} data-testid="monitor-timer">{fmtClock(remaining)}</div>
          <div className="text-neutral-400 uppercase tracking-[0.3em]" style={{ fontSize: "1vw" }}>{(st.phase || "round") === "break" ? t("mon.break") : `${t("mon.round")} ${st.round}`}{st.medical ? ` · ${t("mon.medical")}` : ""}</div>
        </div>
      </div>
      {st.winner && (
        <div className="absolute inset-0 bg-black/80 flex items-center justify-center" data-testid="monitor-winner">
          <div className="text-center">
            <div className="text-gold uppercase tracking-[0.4em] text-2xl">{t("mon.winner")}</div>
            <div className="font-name font-extrabold uppercase text-white leading-none mt-4" style={{ fontSize: "9vw" }}>
              {st.winner === "red" ? nameOf(match, match.red_athlete_id) : nameOf(match, match.blue_athlete_id)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PoomsaeMonitor({ match }) {
  const t = useT();
  const st = match.state;
  if (st.mode === "versus") {
    const side = st.current_side || "red";
    const redAvg = st.red?.done ? st.red.poomsae_average : (side === "red" ? st.poomsae_average : null);
    const blueAvg = st.blue?.done ? st.blue.poomsae_average : (side === "blue" ? st.poomsae_average : null);
    return (
      <div className="h-screen w-screen bg-black flex text-white relative overflow-hidden">
        <PoomsaeVsSide color="red" name={nameOf(match, match.red_athlete_id)} avg={redAvg} active={side === "red" && !st.winner} win={st.winner === "red"} t={t} />
        <PoomsaeVsSide color="blue" name={nameOf(match, match.blue_athlete_id)} avg={blueAvg} active={side === "blue" && !st.winner} win={st.winner === "blue"} t={t} />
        {st.winner && (
          <div className="absolute inset-0 bg-black/80 flex items-center justify-center" data-testid="monitor-winner">
            <div className="text-center">
              <div className="text-gold uppercase tracking-[0.4em] text-2xl">{t("mon.winner")}</div>
              <div className="font-name font-extrabold uppercase text-white leading-none mt-4" style={{ fontSize: "9vw" }}>
                {st.winner === "red" ? nameOf(match, match.red_athlete_id) : nameOf(match, match.blue_athlete_id)}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
  const athleteId = match.athlete_id || match.red_athlete_id;
  return (
    <div className="h-screen w-screen bg-black flex flex-col items-center justify-center text-white px-[4vw]">
      <div className="text-chung uppercase tracking-[0.4em]" style={{ fontSize: "2.2vw" }}>{match.label}</div>
      <div className="font-name font-extrabold uppercase leading-none mt-[1vh] text-center" style={{ fontSize: "10vw" }} data-testid="monitor-poomsae-name">{nameOf(match, athleteId)}</div>

      <div className="flex items-center gap-[4vw] mt-[6vh]">
        <div className="flex flex-col gap-[2.5vh]">
          {st.poomsae_scores.map((s) => (
            <div key={s.form} className={`flex items-center justify-end gap-[2vw] ${s.form === st.current_form ? "opacity-100" : "opacity-60"}`} data-testid={`monitor-poomsae-${s.form}`}>
              <div className="uppercase tracking-widest text-neutral-500 text-right" style={{ fontSize: "1.8vw" }}>{t("mon.form")} {s.form}{s.form === st.current_form ? " ●" : ""}</div>
              <div className="font-score tabular text-white leading-none" style={{ fontSize: "9vw", minWidth: "16vw", textAlign: "right" }}>{s.confirmed ? (s.score ?? "—") : "—"}</div>
            </div>
          ))}
        </div>
        <div className="self-stretch w-px bg-neutral-800" />
        <div className="text-center">
          <div className="uppercase tracking-widest text-gold" style={{ fontSize: "1.8vw" }}>{t("mon.avg")}</div>
          <div key={st.poomsae_average} className="font-score tabular text-gold leading-none mt-2 animate-score" style={{ fontSize: "16vw" }} data-testid="monitor-poomsae-avg">{st.poomsae_average ?? "—"}</div>
        </div>
      </div>
    </div>
  );
}

function PoomsaeVsSide({ color, name, avg, active, win, t }) {
  return (
    <div className={`flex-1 flex flex-col items-center justify-center px-[2vw] ${color === "red" ? "bg-hong/10" : "bg-chung/10"} ${active ? "" : "opacity-60"} ${win ? "ring-4 ring-gold ring-inset" : ""}`}>
      <div className={`uppercase tracking-[0.3em] ${color === "red" ? "text-hong" : "text-chung"}`} style={{ fontSize: "2vw" }}>{color === "red" ? "HONG" : "CHUNG"}</div>
      <div className="font-name font-extrabold uppercase text-white text-center leading-none mt-[1vh]" style={{ fontSize: "5vw" }}>{name}</div>
      <div key={avg} className="font-score tabular text-gold leading-none mt-[3vh] animate-score" style={{ fontSize: "18vw" }}>{avg ?? "—"}</div>
      {active && <div className="text-gold uppercase tracking-[0.3em] mt-[2vh]" style={{ fontSize: "1.4vw" }}>{t("mon.performing")}</div>}
    </div>
  );
}
